import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { StarIcon } from '@heroicons/react/solid';

import axios from '../../config/axios';

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(true);  // نبدأ بـ mobile view
  const [touchStart, setTouchStart] = useState(null);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/reviews');
        setTestimonials(response.data);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
      setIsMobile(width < 768);
    };

    // نتحقق من حجم الشاشة عند تحميل الصفحة
    checkMobile();

    // نتحقق من حجم الشاشة عند تغيير حجم النافذة
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Show reviews if we have any
  if (loading || !testimonials.length) {
    return null;
  }

  return (
    <section className="bg-gray-10 dark:bg-gray-900 py-16">
      <div className={`${isMobile ? 'container mx-auto px-2' : 'max-w-[1150px] mx-auto px-4'}`}>
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12 relative">
            ماذا قال عملاؤنا عنا؟
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 -mb-4 w-16 h-1 bg-primary-500 rounded-full" />
          </h2>
        </div>

        <div className="relative h-[600px] md:h-[500px] flex flex-col items-center justify-center overflow-hidden">
          <div 
            className={`flex ${isMobile ? 'gap-2' : 'gap-6'} items-center transform -translate-y-5 ${isMobile ? 'w-full px-2' : ''}`}
            onTouchStart={(e) => {
              const touch = e.touches[0];
              setTouchStart(touch.clientX);
            }}
            onTouchMove={(e) => {
              if (touchStart === null) return;
              
              const touch = e.touches[0];
              const diff = touchStart - touch.clientX;

              if (Math.abs(diff) > 50) {
                if (diff > 0) {
                  // Swipe left
                  setActiveIndex((prev) => (prev + 1) % testimonials.length);
                } else {
                  // Swipe right
                  setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
                }
                setTouchStart(null);
              }
            }}
          >
            {[...Array(isMobile ? 1 : 5)].map((_, i) => {
              const index = isMobile ? activeIndex : (activeIndex + i - 2 + testimonials.length) % testimonials.length;
              const isCenter = isMobile ? true : i === 2;
              
              return (
                <motion.div
                  key={i}
                  onClick={() => !isCenter && setActiveIndex(index)}
                  className={`cursor-pointer transition-all duration-500 ${
                    isCenter ? `${isMobile ? 'w-[95vw] md:w-full' : 'w-96'} h-[450px] md:h-[400px]` : 'w-80 h-64 md:h-[350px] mt-8'
                  } ${isMobile ? '' : !isCenter ? 'block' : ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ 
                    opacity: isCenter ? 1 : 0.7,
                    y: 0,
                    scale: isCenter ? 1 : 0.9
                  }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="h-full w-full rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className={`h-full w-full p-6 rounded-lg bg-white dark:bg-gray-800 transition-all duration-500 flex flex-col justify-between ${
                      isCenter ? 'shadow-lg dark:shadow-gray-900/50' : ''
                    }`}>
                    <h3 className={`font-semibold text-primary-600 dark:text-primary-400 text-center mb-4 truncate ${
                      isCenter ? 'text-xl' : 'text-lg'
                    }`}>
                      <div className="flex flex-col items-center gap-3 mb-2">
                        {testimonials[index].customer_image && (
                          <div className="w-16 h-16 rounded-full overflow-hidden">
                            <img 
                              src={testimonials[index].customer_image}
                              alt={testimonials[index].customer_name || 'صورة العميل'}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        {testimonials[index].customer_name && (
                          <span>{testimonials[index].customer_name}</span>
                        )}
                      </div>
                    </h3>

                    <div className="flex justify-center mb-4">
                      {[...Array(testimonials[index].rating || 5)].map((_, starIndex) => (
                        <StarIcon
                          key={starIndex}
                          className={`text-yellow-400 dark:text-yellow-500 ${
                            isCenter ? 'w-5 h-5' : 'w-4 h-4'
                          }`}
                        />
                      ))}
                    </div>

                    <div className="flex-1 flex flex-col">
                      {testimonials[index].type === 'image' ? (
                        <div className="flex-1 flex items-center justify-center overflow-hidden">
                          {testimonials[index].content ? (
                            <img 
                              src={testimonials[index].content} 
                              alt="صورة التقييم" 
                              className="max-h-[250px] w-auto object-contain"
                              loading="lazy"
                            />
                          ) : null}
                        </div>
                      ) : (
                        <p className={`text-gray-600 dark:text-gray-300 text-center line-clamp-5 h-full flex items-center justify-center px-4 ${
                          isCenter ? 'text-lg' : 'text-base'
                        }`}>
                          "{testimonials[index].content}"
                        </p>
                      )}
                    </div>
                  </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Dots for navigation - bigger in mobile */}
          <div className="mt-12 flex gap-4">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`${isMobile ? 'w-4 h-4' : 'w-3 h-3'} rounded-full transition-all ${
                  index === activeIndex 
                    ? `bg-primary-500 ${isMobile ? 'w-8 scale-110' : 'w-6'}` 
                    : 'bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500 hover:scale-110'
                }`}
                aria-label={`انتقل إلى التقييم ${index + 1}`}
              />
            ))}
          </div>

          {/* Mobile swipe hint */}
          {isMobile && (
            <div className="mt-4 text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              اسحب يمين ويسار للتنقل
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
